import React, { useState } from 'react';

import {
  PAYMENT_TYPE_OPTIONS,
  PRIOR_AUTH_OPTIONS,
  PRIOR_AUTH_OPTIONS_LIST
} from '@/constants/treatability';
import AutocompleteMultiSelect from '@/ui/components/AutocompleteMultiSelect';
import AutocompleteSingleSelect from '@/ui/components/AutocompleteSingleSelect';
import Input from '@/ui/components/Form/InputField';
import { OptionType } from '@/ui/components/Form/Select/types';
import Modal from '@/ui/components/Modal';

import type { Props } from './types';

const BulkAddModal = ({
  open,
  onClose,
  repo,
  addMessage,
  onSuccess,
  programs,
  jurisdictions,
  providers,
  insuranceTypes
}: Props) => {
  const [selectedPrograms, setSelectedPrograms] = useState<OptionType<string>[]>([]);
  const [selectedJurisdictions, setSelectedJurisdictions] = useState<OptionType<string>[]>([]);
  const [selectedProviders, setSelectedProviders] = useState<OptionType<string>[]>([]);
  const [selectedPaymentTypes, setSelectedPaymentTypes] = useState<OptionType<string>[]>([]);
  const [selectedInsuranceTypes, setSelectedInsuranceTypes] = useState<OptionType<string>[]>([]);
  const [selectedPriorAuth, setSelectedPriorAuth] = useState<OptionType<PRIOR_AUTH_OPTIONS> | null>(
    null
  );
  const [activeOn, setActiveOn] = useState('');
  const [activeUntil, setActiveUntil] = useState('');

  const reset = () => {
    setSelectedPrograms([]);
    setSelectedJurisdictions([]);
    setSelectedProviders([]);
    setSelectedPaymentTypes([]);
    setSelectedInsuranceTypes([]);
    setSelectedPriorAuth(null);
    setActiveOn('');
    setActiveUntil('');
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSubmit = async () => {
    if (selectedPrograms.length === 0) {
      addMessage({ level: 'error', text: 'Please select at least one program.' });
      return;
    }
    if (selectedJurisdictions.length === 0) {
      addMessage({ level: 'error', text: 'Please select at least one jurisdiction.' });
      return;
    }

    const paymentType = selectedPaymentTypes[0]?.value ?? 'insurance';
    const isInsurance = paymentType === 'insurance';
    const priorAuth: 'yes' | 'no' | 'unknown' = selectedPriorAuth?.value ?? 'unknown';
    // Only include insurance provider/type combos when payment_type is insurance;
    // self-pay matches must not carry these fields or they violate DB constraints.
    const providerValues =
      isInsurance && selectedProviders.length > 0
        ? selectedProviders.map((p) => String(p.value))
        : [null];
    const insuranceTypeValues =
      isInsurance && selectedInsuranceTypes.length > 0
        ? selectedInsuranceTypes.map((t) => String(t.value))
        : [null];

    const matchPayloads = selectedPrograms.flatMap((prog) =>
      selectedJurisdictions.flatMap((j) =>
        providerValues.flatMap((provId) =>
          insuranceTypeValues.map((insType) => ({
            program: String(prog.value),
            jurisdiction: j.value,
            ...(provId ? { insurance_provider: provId } : {}),
            payment_type: paymentType,
            ...(insType ? { insurance_type: insType } : {}),
            ...(activeOn ? { active_on: activeOn } : {}),
            ...(activeUntil ? { active_until: activeUntil } : {}),
            prior_auth_required: priorAuth
          }))
        )
      )
    );

    try {
      const result = await repo.bulkCreateTreatabilityMatches(matchPayloads);
      addMessage({
        level: 'success',
        text: `Created ${result.data?.length ?? 0} match${result.data?.length !== 1 ? 'es' : ''}.`
      });
      handleClose();
      onSuccess();
    } catch (e) {
      addMessage({ level: 'error', text: typeof e === 'string' ? e : 'Failed to create matches.' });
    }
  };

  const programOptions: OptionType<string>[] = programs.map((p) => ({
    value: p.id,
    label: p.phase_type_name ? `${p.name} (${p.phase_type_name})` : p.name
  }));
  const jurisdictionOptions: OptionType<string>[] = jurisdictions.map((j) => ({
    value: String(j.id),
    label: j.name
  }));
  const providerOptions: OptionType<string>[] = providers.map((p) => ({
    value: p.id,
    label: p.external_name
  }));
  const matchCount =
    Math.max(selectedPrograms.length, 1) *
    Math.max(selectedJurisdictions.length, 1) *
    Math.max(selectedProviders.length, 1) *
    Math.max(selectedInsuranceTypes.length, 1);

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Add Treatability Matches"
      button={{ children: 'Create Matches', onClick: handleSubmit }}
      dismissButton={{ children: 'Cancel', onClick: handleClose }}>
      <div className="mt-4 space-y-4">
        <AutocompleteMultiSelect
          label="Programs *"
          options={programOptions}
          selectedOptions={selectedPrograms}
          onChange={setSelectedPrograms}
          placeholder="Search programs…"
          debounce={false}
        />
        <AutocompleteMultiSelect
          label="Jurisdictions *"
          options={jurisdictionOptions}
          selectedOptions={selectedJurisdictions}
          onChange={setSelectedJurisdictions}
          placeholder="Search jurisdictions…"
          debounce={false}
        />

        <AutocompleteMultiSelect
          label="Insurance Providers"
          options={providerOptions}
          selectedOptions={selectedProviders}
          onChange={setSelectedProviders}
          placeholder="Any (leave empty for none)"
          debounce={false}
        />

        <AutocompleteMultiSelect
          label="Payment Type"
          options={PAYMENT_TYPE_OPTIONS}
          selectedOptions={selectedPaymentTypes}
          onChange={setSelectedPaymentTypes}
          placeholder="Select payment type…"
          debounce={false}
        />

        <AutocompleteMultiSelect
          label="Insurance Types"
          options={insuranceTypes}
          selectedOptions={selectedInsuranceTypes}
          onChange={setSelectedInsuranceTypes}
          placeholder="Any (leave empty for none)"
          debounce={false}
        />

        <AutocompleteSingleSelect
          label="Prior Auth Required"
          options={PRIOR_AUTH_OPTIONS_LIST}
          selectedOption={selectedPriorAuth}
          onChange={(val) => setSelectedPriorAuth(val as OptionType<PRIOR_AUTH_OPTIONS> | null)}
          placeholder="Select prior auth…"
          debounce={false}
        />

        <Input
          id="bulk-add-active-on"
          label="Active On"
          type="date"
          value={activeOn}
          onChange={(e) => setActiveOn(e.target.value)}
        />

        <Input
          id="bulk-add-active-until"
          label="Active Until"
          type="date"
          value={activeUntil}
          onChange={(e) => setActiveUntil(e.target.value)}
        />

        {(selectedPrograms.length > 0 || selectedJurisdictions.length > 0) && (
          <p className="text-xs text-gray-500">
            {matchCount} match{matchCount !== 1 ? 'es' : ''} will be created (
            {selectedPrograms.length || 1} program{selectedPrograms.length !== 1 ? 's' : ''} ×{' '}
            {selectedJurisdictions.length || 1} jurisdiction
            {selectedJurisdictions.length !== 1 ? 's' : ''}
            {selectedProviders.length > 0
              ? ` × ${selectedProviders.length} provider${selectedProviders.length !== 1 ? 's' : ''}`
              : ''}
            {selectedInsuranceTypes.length > 0
              ? ` × ${selectedInsuranceTypes.length} insurance type${selectedInsuranceTypes.length !== 1 ? 's' : ''}`
              : ''}
            )
          </p>
        )}
      </div>
    </Modal>
  );
};

export default React.memo(BulkAddModal);
